import {
  getZrmListingInfo,
  getZrmRentalsLead,
  getZrmUserInfo,
  type ZrmClickstreamLead,
  type ZrmClickstreamListing,
} from '@zg-rentals/clickstream';
import { trackListingEvent } from '../../analytics';
import { SEMANTIC_EVENT_NAME, TOPIC_TAG_TXT, TRIGGER_LOCATION, TRIGGER_OBJECT, TRIGGER_TYPE } from './globals';
import type { ZrmClickstreamUserOrNull } from '../../analytics/types';

const EVENT_TEMPLATE_ID = '168';

// https://dataportal.zillowgroup.net/event-registry/5128
export const CLICK_ON_LATEST_CONTACT_COLUMN_EVENT_TYPE_ID = '5128';
export const emitClickOnLatestContactColumnEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  lead?: ZrmClickstreamLead | null,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: CLICK_ON_LATEST_CONTACT_COLUMN_EVENT_TYPE_ID,
      event_type_version_id: '3',
      event_template_id: '628',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'latest_contact_column_link',
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ManageLead,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

// https://dataportal.zillowgroup.net/event-registry/5129
export const CLICK_ON_NEXT_STEP_COLUMN_EVENT_TYPE_ID = '5129';
export const emitClickOnNextStepColumnEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerObjectName: string,
  topicTag: TOPIC_TAG_TXT.ZrmSatellite | TOPIC_TAG_TXT.ZrmTourScheduling,
  lead?: ZrmClickstreamLead | null,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: CLICK_ON_NEXT_STEP_COLUMN_EVENT_TYPE_ID,
      event_type_version_id: '7',
      event_template_id: '628',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'next_step_column_link',
      trigger_object_nm: triggerObjectName,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ManageLead,
      topic_tag_txt: [topicTag],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

// https://dataportal.zillowgroup.net/event-registry/5133
export const SEARCH_FOR_A_LEAD_EVENT_TYPE_ID = '5133';
export const emitSearchForALeadEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  lead?: ZrmClickstreamLead | null,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: SEARCH_FOR_A_LEAD_EVENT_TYPE_ID,
      event_type_version_id: '3',
      event_template_id: '628',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'search_for_a_lead',
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.FilterLead,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
    ...getZrmRentalsLead(lead),
  });

// https://dataportal.zillowgroup.net/event-registry/5125
export const FILTER_LEADS_BY_MATCHING_CRITERIA_EVENT_TYPE_ID = '5125';
export const emitFilterLeadsByMatchingCriteriaEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerObjectName: string,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: FILTER_LEADS_BY_MATCHING_CRITERIA_EVENT_TYPE_ID,
      event_type_version_id: '4',
      event_template_id: EVENT_TEMPLATE_ID,
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'criteria_match_filter',
      trigger_object_nm: triggerObjectName,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.FilterLead,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/5126
export const FILTER_LEADS_BY_STATUS_IN_PROCESS_EVENT_TYPE_ID = '5126';
export const emitFilterLeadsByStatusInProcessEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerObjectName: string,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: FILTER_LEADS_BY_STATUS_IN_PROCESS_EVENT_TYPE_ID,
      event_type_version_id: '4',
      event_template_id: EVENT_TEMPLATE_ID,
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'status_filter',
      trigger_object_nm: triggerObjectName,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.FilterLead,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/5127
export const FILTER_LEADS_BY_MESSAGE_TYPE_EVENT_TYPE_ID = '5127';
export const emitFilterLeadsByMessageTypeEvent = (
  user: ZrmClickstreamUserOrNull,
  listing: ZrmClickstreamListing,
  triggerObjectName: string,
) =>
  trackListingEvent({
    envelope: {
      event_type_id: FILTER_LEADS_BY_MESSAGE_TYPE_EVENT_TYPE_ID,
      event_type_version_id: '4',
      event_template_id: EVENT_TEMPLATE_ID,
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'message_type_filter',
      trigger_object_nm: triggerObjectName,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.FilterLead,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/9042
export const CLICK_ON_SHARE_FEEDBACK_EVENT_TYPE_ID = '9042';
export const emitClickOnShareFeedbackEvent = (user: ZrmClickstreamUserOrNull, listing: ZrmClickstreamListing) =>
  trackListingEvent({
    envelope: {
      event_type_id: CLICK_ON_SHARE_FEEDBACK_EVENT_TYPE_ID,
      event_type_version_id: '4',
      event_template_id: EVENT_TEMPLATE_ID,
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'share_feedback_link',
      trigger_object_nm: TRIGGER_OBJECT.FeedbackNotificationBanner,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.ShareFeedback,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });

// https://dataportal.zillowgroup.net/event-registry/9043
export const SELECT_PROPERTY_EVENT_TYPE_ID = '9043';
export const emitSelectPropertyEvent = (user: ZrmClickstreamUserOrNull, listing: ZrmClickstreamListing) =>
  trackListingEvent({
    envelope: {
      event_type_id: SELECT_PROPERTY_EVENT_TYPE_ID,
      event_type_version_id: '4',
      event_template_id: EVENT_TEMPLATE_ID,
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: TRIGGER_TYPE.Interaction,
      trigger_location_nm: TRIGGER_LOCATION.LeadManagementPage,
      trigger_source_nm: 'dropdown_to_choose_property',
      trigger_object_nm: TRIGGER_OBJECT.NoTriggerObject,
    },
    semantic: {
      semantic_event_nm: SEMANTIC_EVENT_NAME.FilterLead,
      topic_tag_txt: [TOPIC_TAG_TXT.ZrmSatellite],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(listing),
  });
